import { CarouselNavigation } from "@/components/carousel-navigation";
import { SellerUnitOne } from "@/components/sup/one";
import { SellerUnitOneSkeleton } from "@/components/sup/skeletons/seller-unit-one-skeleton";
import { CarouselHeaderSkeleton, CarouselNavigationSkeleton } from "@/components/ui/skeleton";
import { useSupOne } from "@/hooks/use-sup-data";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import { MapItem, MapItemSUP } from "@/types/search";
import type { SellerUnitAndProject, SellerUnitsAndProjectsOne } from "@/types/sup";
import { propertyUrlSync } from "@/utils/property-url-sync";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface PropertyDetailsCarouselProps {
	supIds: string[];
	projectName?: string | null;
	onToggleFavorite?: (property: MapItemSUP) => void;
	onShortlist?: (property: MapItemSUP) => void;
	className?: string;
	isForPreference?: boolean; // When true, disables search page store integration
}

// Memoized carousel item component for better performance
const CarouselItem = memo(
	({
		sup,
		position,
		isLoaded,
		slideDirection,
	}: {
		sup: SellerUnitsAndProjectsOne;
		position: "prev" | "current" | "next";
		isLoaded: boolean;
		slideDirection: "left" | "right" | null;
		loading: boolean;
	}) => {
		if (!sup) return null;

		let translateX = "0%";
		let opacity = 1;

		if (position === "prev") {
			translateX = slideDirection === "right" ? "0%" : "-100%";
			opacity = slideDirection === "right" ? 1 : 0;
		} else if (position === "current") {
			if (slideDirection === "left") {
				translateX = "-100%";
				opacity = 0;
			} else if (slideDirection === "right") {
				translateX = "100%";
				opacity = 0;
			} else {
				translateX = "0%";
				opacity = 1;
			}
		} else if (position === "next") {
			translateX = slideDirection === "left" ? "0%" : "100%";
			opacity = slideDirection === "left" ? 1 : 0;
		}

		return (
			<div
				key={`${sup._id}-${position}`}
				className="absolute top-0 left-0 w-full h-full transition-all duration-300 ease-in-out"
				style={{
					transform: `translateX(${translateX})`,
					opacity,
					willChange: "transform, opacity", // Optimize for animations
				}}
			>
				{/* Always show content if sup data is available, regardless of loading state */}
				{sup && isLoaded ? (
					<SellerUnitOne
						supOne={sup}
						className="border-0 shadow-none rounded-none"
						isLoading={false}
						isInModal
						isVisible={position === "current"} // Only track when visible
					/>
				) : (
					<SellerUnitOneSkeleton isInModal />
				)}
			</div>
		);
	},
);

CarouselItem.displayName = "CarouselItem";

export function PropertyDetailsCarousel({
	supIds,
	projectName,
	className = "",
	isForPreference = false,
}: PropertyDetailsCarouselProps) {
	// Get overlaySupId from property interaction store
	const overlaySupId = usePropertyInteractionStore(state => state.overlaySupId);
	const addViewedProperty = usePropertyInteractionStore(state => state.addViewedProperty);

	// Sort supIds to prioritize overlaySupId first for better UX
	const sortedSupIds = useMemo(() => {
		if (!overlaySupId || isForPreference) return supIds;

		return [overlaySupId, ...supIds.filter(id => id !== overlaySupId)];
	}, [supIds, overlaySupId, isForPreference]);

	// Get initial index - should always be 0 now since we sort the array
	const getInitialIndex = () => {
		if (isForPreference) return 0;
		const currentPropertyId = propertyUrlSync.getCurrentPropertyId();
		if (currentPropertyId) {
			const index = sortedSupIds.findIndex(id => id === currentPropertyId);
			return index >= 0 ? index : 0;
		}
		return 0;
	};

	const [currentIndex, setCurrentIndex] = useState(getInitialIndex);
	const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(null);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const currentPropertyIdRef = useRef<string | null>(null);
	const initialViewedRef = useRef(false);
	const isOpen = usePropertyInteractionStore(state => state.isPropertyOverlayOpen);

	// For preference context, we use simplified approach without search page stores
	const mapData: MapItem[] = [];

	// Find initial data from map data (only when available)
	const initialData =
		mapData.length > 0
			? sortedSupIds
					.map(id => {
						for (const mapItem of mapData) {
							const foundSup = mapItem.sups.find((sup: SellerUnitAndProject) => sup._id === id);
							if (foundSup) {
								return {
									...foundSup,
									projectName: mapItem.projectName,
									projectId: mapItem.projectId,
									location: {
										...foundSup.location,
										gps: {
											type: "Point" as const,
											coordinates: Array.isArray(mapItem._id)
												? mapItem._id
												: (String(mapItem._id).split(",").map(Number) as [number, number]),
										},
									},
								};
							}
						}
						return null;
					})
					.filter((sup): sup is NonNullable<typeof sup> => sup !== null)
			: [];

	// Optimized data fetching strategy: Load current and adjacent items first
	const getPriorityIds = useCallback(
		(index: number): string[] => {
			const priorityIds: string[] = [];
			const totalItems = sortedSupIds.length;

			// Always include current item first
			priorityIds.push(sortedSupIds[index]);

			// Add adjacent items for smooth transitions
			if (totalItems > 1) {
				const prevIndex = index === 0 ? totalItems - 1 : index - 1;
				const nextIndex = index === totalItems - 1 ? 0 : index + 1;

				priorityIds.push(sortedSupIds[nextIndex]);
				priorityIds.push(sortedSupIds[prevIndex]);
			}

			// Add remaining items for background loading
			sortedSupIds.forEach(id => {
				if (!priorityIds.includes(id)) {
					priorityIds.push(id);
				}
			});

			return priorityIds;
		},
		[sortedSupIds],
	);

	// Fetch SUP data with priority loading
	const priorityIds = getPriorityIds(currentIndex);
	const initData = initialData.length > 0 ? initialData : undefined;
	const {
		data: sups,
		loading,
		initialLoading,
		error,
	} = useSupOne(priorityIds, initData as SellerUnitAndProject[] | undefined);

	// Reorder sups to match original sortedSupIds order for consistent indexing
	const orderedSups = useMemo(() => {
		if (sups.length === 0) return [];

		return sortedSupIds
			.map(id => sups.find(sup => sup._id === id))
			.filter((sup): sup is NonNullable<typeof sup> => sup !== undefined);
	}, [sups, sortedSupIds]);

	// Calculate view count from viewed properties using granular selector
	const viewCount = usePropertyInteractionStore(
		useCallback(
			state => {
				return sortedSupIds.filter(id => state.viewedProperties.has(id)).length;
			},
			[sortedSupIds],
		),
	);

	// Mark initial property as viewed when carousel first opens (only once)
	useEffect(() => {
		if (!initialViewedRef.current) {
			const initialPropertyId = sortedSupIds[currentIndex];
			const { viewedProperties } = usePropertyInteractionStore.getState();
			if (initialPropertyId && !viewedProperties.has(initialPropertyId)) {
				addViewedProperty(initialPropertyId);
			}
			initialViewedRef.current = true;
		}
	}, [sortedSupIds, currentIndex, addViewedProperty]);

	// Memoize current property ID to prevent unnecessary effects
	const currentPropertyId = useMemo(() => {
		return orderedSups[currentIndex]?._id;
	}, [orderedSups, currentIndex]);

	// Update URL and mark property as viewed for property navigation (manual search only)
	useEffect(() => {
		if (currentPropertyId && isOpen) {
			// Mark current property as viewed immediately (skip if already viewed)
			const { viewedProperties } = usePropertyInteractionStore.getState();
			if (!viewedProperties.has(currentPropertyId)) {
				addViewedProperty(currentPropertyId);
			}

			// Debounce URL updates to prevent excessive re-renders
			if (currentPropertyIdRef.current !== currentPropertyId) {
				propertyUrlSync.updatePropertyIdDebounced(currentPropertyId);
				currentPropertyIdRef.current = currentPropertyId;
			}
		}
	}, [currentPropertyId, isOpen, addViewedProperty]);

	const goToPrevious = useCallback(() => {
		if (isTransitioning) return;

		setIsTransitioning(true);
		setSlideDirection("left");
		setCurrentIndex(prev => (prev === 0 ? orderedSups.length - 1 : prev - 1));

		setTimeout(() => {
			setSlideDirection(null);
			setIsTransitioning(false);
		}, 300);
	}, [orderedSups.length, isTransitioning]);

	const goToNext = useCallback(() => {
		if (isTransitioning) return;

		setIsTransitioning(true);
		setSlideDirection("right");
		setCurrentIndex(prev => (prev > orderedSups.length - 1 ? 0 : prev + 1));

		setTimeout(() => {
			setSlideDirection(null);
			setIsTransitioning(false);
		}, 300);
	}, [orderedSups.length, isTransitioning]);

	const goToIndex = useCallback(
		(index: number) => {
			if (index === currentIndex || isTransitioning) return;

			setIsTransitioning(true);
			const direction = index > currentIndex ? "right" : "left";
			setSlideDirection(direction);
			setCurrentIndex(index);

			setTimeout(() => {
				setSlideDirection(null);
				setIsTransitioning(false);
			}, 300);
		},
		[currentIndex, isTransitioning],
	);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (orderedSups.length <= 1 || isTransitioning) return;

			if (event.key === "ArrowLeft") {
				event.preventDefault();
				goToPrevious();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				goToNext();
			}
		},
		[orderedSups.length, goToPrevious, goToNext, isTransitioning],
	);

	// Add keyboard event listeners
	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	// Get the three sups we need to render (prev, current, next) with loading states
	const getVisibleSups = useCallback(() => {
		if (orderedSups.length === 0) return [];
		if (orderedSups.length === 1)
			return [{ sup: orderedSups[0], position: "current" as const, isLoaded: !!orderedSups[0] }];

		const prevIndex = currentIndex === 0 ? orderedSups.length - 1 : currentIndex - 1;
		const nextIndex = currentIndex === orderedSups.length - 1 ? 0 : currentIndex + 1;

		return [
			{
				sup: orderedSups[prevIndex],
				position: "prev" as const,
				isLoaded: !!orderedSups[prevIndex],
			},
			{
				sup: orderedSups[currentIndex],
				position: "current" as const,
				isLoaded: !!orderedSups[currentIndex],
			},
			{
				sup: orderedSups[nextIndex],
				position: "next" as const,
				isLoaded: !!orderedSups[nextIndex],
			},
		].filter(item => item.sup); // Filter out undefined sups
	}, [orderedSups, currentIndex]);

	const visibleSups = getVisibleSups();

	if (error || (!loading && !initialLoading && orderedSups?.length === 0)) {
		return (
			<div className={`bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className}`}>
				<div className="flex items-center justify-center h-64">
					<div className="text-gray-500">Unable to fetch property details</div>
				</div>
			</div>
		);
	}

	// Show skeleton only when we have no data at all and are still in initial loading
	if (initialLoading && orderedSups?.length === 0) {
		return (
			<div className={`bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className}`}>
				{/* Header skeleton with project name and count */}
				<CarouselHeaderSkeleton />

				{/* Navigation skeleton */}
				<CarouselNavigationSkeleton />

				{/* Content skeleton */}
				<SellerUnitOneSkeleton isInModal />
			</div>
		);
	}

	// If only one property, show the single property unit content
	if (orderedSups.length === 1 && orderedSups[0]) {
		return (
			<div className={`bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className} relative`}>
				{/* Mobile: scrollable container, Desktop: natural flow */}
				<div
					className="md:contents overflow-y-auto overflow-x-hidden md:overflow-visible"
					style={{ height: "calc(100dvh - 94px)" }}
				>
					<SellerUnitOne
						supOne={orderedSups[0]}
						className="border-0 shadow-none rounded-none"
						isLoading={false} // Don't show loading state if we have data
						isInModal
					/>
				</div>
			</div>
		);
	}

	return (
		<div className={`bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className} relative`}>
			{/* Header with project name - Show even during loading */}
			<div className="flex items-center justify-between p-2 pt-4 border-b">
				<a
					href={`/projects/${orderedSups[0]?.projectId}`}
					target="_blank"
					rel="noopener noreferrer"
					className="text-sm text-primary hover:underline"
				>
					<h3 className="font-semibold text-gray-900">{projectName || orderedSups[0]?.projectName || "Properties"}</h3>
				</a>
				<p className="text-sm text-gray-500">
					{currentIndex + 1} of {sortedSupIds.length}
					{viewCount > 0 && ` • Viewed ${viewCount}`}
				</p>
			</div>

			{/* Navigation Controls - Show even during loading */}
			<CarouselNavigation
				currentIndex={currentIndex}
				totalItems={orderedSups.length || sortedSupIds.length}
				onPrevious={goToPrevious}
				onNext={goToNext}
				onGoToIndex={goToIndex}
				disabled={isTransitioning} // Only disable during transitions, not during loading
			/>

			{/* Property Content */}
			<div
				className="relative overflow-y-auto overflow-x-hidden"
				style={{ height: "calc(100dvh - 94px)", touchAction: "pan-y" }}
			>
				{/* Show content immediately if we have any data, otherwise show skeleton */}
				{orderedSups.length === 0 && initialLoading ? (
					<SellerUnitOneSkeleton isInModal />
				) : (
					<div
						className={`relative w-full h-full ${visibleSups.length > 1 ? "overflow-y-scroll overflow-x-hidden" : "overflow-hidden"}`}
					>
						<div className="relative w-full h-full">
							{visibleSups.map(({ sup, position, isLoaded }) => (
								<CarouselItem
									key={`${sup?._id}-${position}`}
									sup={sup}
									position={position}
									isLoaded={isLoaded}
									slideDirection={slideDirection}
									loading={loading}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
